import queryString from "query-string";
import AxiosInstance, { takeLatestRequest } from "@/config/AxiosInstance";
import { API_BASE_URL_ADMIN, API_BASE_URL_REPORT } from "@/config/appConfig";

type ReportQuery = Record<string, unknown>;
type ReportId = string | number;
type CampaignReportQuery = ReportQuery & {
  campaign_id?: ReportId;
  channel_id?: ReportId;
  downloadReport?: boolean;
  filter?: string;
  fk_brand_id?: ReportId;
  fk_participant_id?: ReportId;
  group_id?: ReportId;
  location_id?: ReportId;
  system_generated_campaign_id?: string;
};
type WidgetDrillQuery = CampaignReportQuery & {
  drill_type?: string;
  widgetId?: ReportId;
};

export const reportService = {
  getBrandReport,
  getCompanyReport,
  getLocationReport,
  getCampaignReport,
  getCampaignReportDetail,
  getUniversalWidgetReport,
  getParticipantDashboardWidget,
  getPostcardCampaignReport,
  getDashboardReport,
  getOverviewDashboardReport,
  getWidgetDrillUpDownData,
};

function getBrandReport(reqData: ReportQuery, isSWR = false) {
  const query = queryString.stringify(reqData);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/brandWidgetReport?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/brandWidgetReport?${query}`);
}

function getCompanyReport(id: ReportId) {
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/companyReport?id=${id}`);
}

function getLocationReport(data: ReportQuery, isSWR = false) {
  const query = queryString.stringify({ ...data });
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/locationWidgetReport?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/locationWidgetReport?${query}`
  );
}

function getCampaignReport(data: CampaignReportQuery, isSWR = false) {
  const getData = {
    campaign_id: "",
    system_generated_campaign_id: "",
    fk_brand_id: "",
    fk_participant_id: "",
    location_id: "",
    group_id: "",
    channel_id: "",
    filter: "",
    downloadReport: false,
    ...data,
  };
  const config = {
    method: "get",
    url: `${API_BASE_URL_REPORT}/campaignWidgetReportTemplate`,
    params: getData,
  };
  if (isSWR) {
    return takeLatestRequest(config, "campaignWidgetReportTemplate").then(
      (r) => r.data
    );
  }
  return takeLatestRequest(config, "campaignWidgetReportTemplate");
}

function getCampaignReportDetail(data: CampaignReportQuery, isSWR = false) {
  const getData = {
    campaign_id: "",
    system_generated_campaign_id: "",
    fk_brand_id: "",
    fk_participant_id: "",
    location_id: "",
    group_id: "",
    channel_id: "",
    filter: "",
    downloadReport: false,
    ...data,
  };
  const config = {
    method: "get",
    url: `${API_BASE_URL_REPORT}/campaignWidgetReportPauseWithHeader`,
    params: getData,
  };
  if (isSWR) {
    return takeLatestRequest(
      config,
      "campaignWidgetReportPauseWithHeader"
    ).then((r) => r.data);
  }
  return takeLatestRequest(config, "campaignWidgetReportPauseWithHeader");
}

function getWidgetDrillUpDownData(data: WidgetDrillQuery, isSWR = false) {
  const getData = {
    campaign_id: "",
    system_generated_campaign_id: "",
    fk_brand_id: "",
    fk_participant_id: "",
    location_id: "",
    group_id: "",
    channel_id: "",
    filter: "",
    downloadReport: false,
    widgetId: "",
    drill_type: "",
    ...data,
  };
  const config = {
    method: "get",
    url: `${API_BASE_URL_ADMIN}/widgetReportDrillUpDown`,
    params: getData,
  };
  if (isSWR) {
    return takeLatestRequest(config, "widgetReportDrillUpDown").then(
      (r) => r.data
    );
  }
  return takeLatestRequest(config, "widgetReportDrillUpDown");
}

function getUniversalWidgetReport(reqData: ReportQuery, isSWR = false) {
  const query = queryString.stringify(reqData);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/universalWidegtReport?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/universalWidegtReport?${query}`
  );
}

function getParticipantDashboardWidget(data: ReportQuery, isSWR = false) {
  const query = queryString.stringify({ ...data });
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/participantWidgetReport?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/participantWidgetReport?${query}`
  );
}

function getDashboardReport(data: ReportQuery, isSWR = false) {
  const query = queryString.stringify({ ...data });
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/dashboardReport?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/dashboardReport?${query}`);
}

function getOverviewDashboardReport(data: ReportQuery, isSWR = false) {
  const query = queryString.stringify({ ...data });
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/overview-dashboard-report?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/overview-dashboard-report?${query}`
  );
}

function getPostcardCampaignReport(data: CampaignReportQuery) {
  const getData = {
    campaign_id: "",
    system_generated_campaign_id: "",
    fk_brand_id: "",
    fk_participant_id: "",
    location_id: "",
    group_id: "",
    channel_id: "",
    filter: "",
    downloadReport: false,
    ...data,
  };
  const config = {
    method: "get",
    url: `${API_BASE_URL_ADMIN}/postcardCampaignWidgetReport`,
    params: getData,
  };
  return takeLatestRequest(config, "postcardCampaignWidgetReport");
}
